import { __assign, __rest, __awaiter, __generator } from '../../node_modules/tslib/tslib.es6.js';
import { BrandclubPageType, PageStatus } from '@brandclub/types';
import { styled, Grid, useTheme, Backdrop, Popover } from '@mui/material';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { AnalyticsTrackingEvent } from '../../analytics/events.js';
import brandclub_colors from '../../colors/brandclubColors.js';
import { BookmarkStarIcon } from '../../icons/BookmarkStarIcon.js';
import { SmartphoneIcon } from '../../icons/SmartphoneIcon.js';
import { brandclubStyles } from '../../styles/index.js';
import { FilterParam } from '../../types/filters.js';
import 'uuid';
import 'lodash/includes';
import { getBrandLogo, BC_VIDEOS } from '../../utils/assetUtils.js';
import '../../utils/brandclubStoreConfigUtils.js';
import { formatRewardAmount } from '../../utils/brandUtils.js';
import '../../utils/broadcast.js';
import { TABLET_SCREEN_SIZE, TABLET_HORIZONTAL_PADDING, MOBILE_SCREEN_SIZE, MOBILE_HORIZONTAL_PADDING, DESKTOP_SCREEN_SIZE, DESKTOP_HORIZONTAL_PADDING } from '../../utils/constants/Constants.js';
import 'awesome-phonenumber';
import 'lodash/isEqual';
import 'lodash/get';
import 'lodash/orderBy';
import '../../types/Campaign.js';
import '../../types/product.js';
import { escapeUriPath } from '../../utils/stringUtils.js';
import { sendTextCallback } from '../../utils/textMessage.js';
import { GenericContainer } from '../components/GenericContainer.js';
import { ProductPageDialog } from '../components/ProductPageDialog/ProductPageDialog.js';
import NavSearchButton from './Buttons/NavSearchButton.js';
import NavSearchCloseButton from './Buttons/NavSearchCloseButton.js';
import NavUserButton from './Buttons/NavUserButton.js';
import CategoriesDropdown from './CategoriesDropdown.js';
import ExploreDropdown from './ExploreDropdown.js';
import SearchDropdown from './SearchDropdown.js';

var _a, _b, _c, _d, _e;
var showHideNav = function (nav) {
    var _a;
    var hasChildPages = (_a = nav.childPages) === null || _a === void 0 ? void 0 : _a.some(function (childPage) { return childPage.status === PageStatus.Visible; });
    if (['Collection', 'Editorial'].includes(nav.pageType)) {
        if (nav.status === PageStatus.Visible) {
            return true;
        }
        return false;
    }
    if (nav.route.startsWith('/subcategories')) {
        // after grouped categories nav updates subcategories should always be hidden
        // sites created prior to this update have the default value of showInNavigation set to true
        return false;
    }
    if (nav.pageType === BrandclubPageType.CustomCategoryGroup) {
        return nav.status === PageStatus.Visible && hasChildPages;
    }
    return nav.showInNavigation && nav.status === PageStatus.Visible;
};
var buildMenuNav = function (mainEntity, brandEntity, pages, persistentQueryParams) {
    var _a, _b;
    var routePrefix = "/".concat(escapeUriPath((_a = brandEntity === null || brandEntity === void 0 ? void 0 : brandEntity.entity) === null || _a === void 0 ? void 0 : _a.name), "/b").concat(((brandEntity === null || brandEntity === void 0 ? void 0 : brandEntity.id) || '').toString().startsWith('/') ? '' : '/').concat(brandEntity === null || brandEntity === void 0 ? void 0 : brandEntity.id);
    return (_b = pages === null || pages === void 0 ? void 0 : pages.filter(function (x) { return showHideNav(x); })) === null || _b === void 0 ? void 0 : _b.map(function (_a) {
        var childPages = _a.childPages, widgets = _a.widgets, route = _a.route, displayName = _a.displayName, previewImageUrl = _a.previewImageUrl, metaDescription = _a.metaDescription;
        var subNav = childPages ? buildMenuNav(mainEntity, brandEntity, childPages, persistentQueryParams) : undefined;
        var routeForLink = (widgets === null || widgets === void 0 ? void 0 : widgets.length) ? "".concat(routePrefix).concat(route) : '';
        if (persistentQueryParams && routeForLink !== '') {
            if (routeForLink.indexOf('?') > 0) {
                routeForLink = "".concat(routeForLink, "&").concat(persistentQueryParams || '');
            }
            else {
                routeForLink = "".concat(routeForLink, "?").concat(persistentQueryParams || '');
            }
        }
        return { name: displayName, route: routeForLink, subNav: subNav, previewImageUrl: previewImageUrl, metaDescription: metaDescription };
    });
};
var BarLeft = styled(Grid)((_a = {
        alignItems: 'center',
        justifyContent: 'flex-start',
        flexWrap: 'nowrap'
    },
    // width: 'fit-content',
    _a["@media screen and (max-width: ".concat(TABLET_SCREEN_SIZE, "px)")] = {
        flexDirection: 'row-reverse',
    },
    _a));
var BarRight = styled(Grid)(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            userSelect: 'none',
            alignItems: 'center',
            justifyContent: 'flex-end'
        },
        _b["@media screen and (max-width: ".concat(TABLET_SCREEN_SIZE, "px)")] = {
            display: 'none',
        },
        _b['.breaker'] = {
            margin: '0 15px',
            borderLeft: "1.5px solid ".concat(theme.palette.primary.main),
            height: '18px',
        },
        _b.a = {
            fontSize: 16,
            fontWeight: 500,
            textDecoration: 'none',
            color: theme.palette.primary.main,
        },
        _b);
});
var BarCenter = styled(Grid)({
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    position: 'absolute',
    width: 'fit-content',
});
var NavigationContainer = styled(GenericContainer)(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            color: theme.palette.primary.main,
            zIndex: 1,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            padding: "0 5%",
            boxSizing: 'border-box'
        },
        _b["@media screen and (max-width: ".concat(TABLET_SCREEN_SIZE, "px)")] = {
            display: 'none',
            padding: "0 ".concat(TABLET_HORIZONTAL_PADDING, "px"),
        },
        _b["@media screen and (max-width: ".concat(MOBILE_SCREEN_SIZE, "px)")] = {
            display: 'none',
            padding: "0 ".concat(MOBILE_HORIZONTAL_PADDING, "px"),
        },
        _b);
});
var NavBarContainer = styled(Grid)((_b = {
        position: 'relative',
        maxWidth: DESKTOP_SCREEN_SIZE - DESKTOP_HORIZONTAL_PADDING * 2,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 85
    },
    // borderBottom: `2px solid ${brandclub_colors.blueWhite}`,
    _b["@media screen and (max-width: ".concat(TABLET_SCREEN_SIZE, "px)")] = {},
    _b['.nav_button'] = {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        fontSize: 18,
        fontWeight: 600,
        padding: '0 20px',
        cursor: 'pointer',
    },
    _b));
var hasRewardAmount = function (rewardAmount) {
    return (rewardAmount === null || rewardAmount === void 0 ? void 0 : rewardAmount.rewardAmount) > 0 || (rewardAmount === null || rewardAmount === void 0 ? void 0 : rewardAmount.rewardPercent) > 0;
};
var HeadBarContainer = styled(GenericContainer)(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            color: theme.palette.primary.main,
            background: brandclub_colors.blueWhite,
            zIndex: 1,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            padding: "0 5%",
            boxSizing: 'border-box',
            marginBottom: 0,
            height: 45
        },
        _b["@media screen and (max-width: ".concat(TABLET_SCREEN_SIZE, "px)")] = {
            display: 'none',
        },
        _b["@media screen and (max-width: ".concat(MOBILE_SCREEN_SIZE, "px)")] = {
            display: 'none',
        },
        _b);
});
var HeaderBarInner = styled(Grid)((_c = {
        position: 'relative',
        maxWidth: DESKTOP_SCREEN_SIZE - DESKTOP_HORIZONTAL_PADDING * 2,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    _c["@media screen and (max-width: ".concat(TABLET_SCREEN_SIZE, "px)")] = {
        flexDirection: 'row-reverse',
    },
    _c));
var HeaderLink = styled('div')({
    display: 'flex',
    alignItems: 'center',
    marginLeft: 25,
    fontSize: 14,
});
var BrandLogo = styled('div')((_d = {
        height: 50,
        border: '2.5px solid #ffffff'
    },
    _d["@media screen and (max-width: ".concat(TABLET_SCREEN_SIZE, "px)")] = {
        position: 'relative',
        top: 'unset',
        left: 'unset',
        width: 42,
        height: 42,
    },
    _d.img = (_e = {
            objectFit: 'contain',
            height: 50
        },
        _e["@media screen and (max-width: ".concat(TABLET_SCREEN_SIZE, "px)")] = {
            width: 42,
            height: 42,
        },
        _e),
    _d));
// eslint-disable-next-line @typescript-eslint/no-unused-vars
var BrandclubNavigationNew = function (_a) {
    var _b, _c, _d;
    var site = _a.site, LinkComponent = _a.LinkComponent, useNavigate = _a.useNavigate, useSearchParams = _a.useSearchParams, mainEntity = _a.mainEntity, brandEntity = _a.brandEntity, axios = _a.axios, trackingParams = _a.trackingParams, analyticsTrack = _a.analyticsTrack, persistentQueryParams = _a.persistentQueryParams, pageContext = _a.pageContext, apolloClient = _a.apolloClient, routePrefix = _a.routePrefix;
    var _e = useState(false), popupOpen = _e[0], setPopupOpen = _e[1];
    var _f = useState(false), filterDialogOpen = _f[0], setFilterDialogOpen = _f[1];
    var _g = useState(0), filterCount = _g[0], setFilterCount = _g[1];
    var _h = useState(null), categoriesAnchorEl = _h[0], setCategoriesAnchorEl = _h[1];
    var _j = useState(null), searchAnchorEl = _j[0], setSearchAnchorEl = _j[1];
    var _k = useState(null), exploreAnchorEl = _k[0], setExploreAnchorEl = _k[1];
    var _l = useState(''), searchInput = _l[0], setSearchInput = _l[1];
    var ref = useRef(null);
    var brandId = (_b = brandEntity === null || brandEntity === void 0 ? void 0 : brandEntity.entity) === null || _b === void 0 ? void 0 : _b.brandId;
    var rewardAmounts = (_c = brandEntity === null || brandEntity === void 0 ? void 0 : brandEntity.entity) === null || _c === void 0 ? void 0 : _c.rewardAmounts;
    var hasJoinReward = hasRewardAmount(rewardAmounts === null || rewardAmounts === void 0 ? void 0 : rewardAmounts.join) || false;
    var rewardAmount = hasJoinReward ? formatRewardAmount(rewardAmounts.join, true) : '';
    var searchParams = useSearchParams()[0];
    var navigate = useNavigate();
    var searchTerm = searchParams.get(FilterParam.TitleInclude);
    var theme = useTheme();
    useEffect(function () {
        setSearchInput(searchTerm || '');
    }, [searchTerm]);
    var handlePopupClose = function () { return setPopupOpen(false); };
    var handleSendText = function (phoneNumber) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, sendTextCallback(phoneNumber, axios, brandEntity, analyticsTrack, trackingParams, AnalyticsTrackingEvent.WEB_APP_SOCIAL_LINK_TEXT_APP_DOWNLOAD_LINK)];
        });
    }); };
    var handleCategoriesOpen = function (event) {
        setCategoriesAnchorEl(event.currentTarget);
    };
    var handleCategoriesClose = function () {
        setCategoriesAnchorEl(null);
    };
    var handleSearchOpen = function (event) {
        setSearchAnchorEl(event.currentTarget);
    };
    var handleSearchClose = function () {
        setSearchAnchorEl(null);
    };
    var handleExploreOpen = function (event) {
        setExploreAnchorEl(event.currentTarget);
    };
    var handleExploreClose = function () {
        setExploreAnchorEl(null);
    };
    var onSubmitSearch = useCallback(function (newKeyword) {
        handleSearchClose();
        var refinedInput = searchInput.trim();
        if (newKeyword) {
            refinedInput = newKeyword.trim();
        }
        if (!refinedInput) {
            return;
        }
        refinedInput = encodeURIComponent(refinedInput);
        var searchRoute = routePrefix + "/search?".concat(FilterParam.TitleInclude, "=").concat(refinedInput);
        if (pageContext.subCategoryId) {
            searchRoute += "&subCategoryId[]=".concat(pageContext.subCategoryId);
        }
        if (pageContext.categoryId) {
            searchRoute += "&categoryId[]=".concat(pageContext.categoryId);
        }
        if (persistentQueryParams) {
            searchRoute += "&".concat(persistentQueryParams);
        }
        analyticsTrack === null || analyticsTrack === void 0 ? void 0 : analyticsTrack(AnalyticsTrackingEvent.SELECTED_SEARCH_TERM_FROM_DROPDOWN, __assign(__assign({}, trackingParams), { searchTerm: refinedInput, searchRoute: searchRoute }));
        navigate("".concat(searchRoute));
    }, [searchInput, navigate, handleSearchClose]);
    var navMenu = buildMenuNav(mainEntity, brandEntity, site.pages, persistentQueryParams);
    var searchOpen = Boolean(searchAnchorEl);
    return (React.createElement(React.Fragment, null,
        React.createElement(Backdrop, { open: Boolean(categoriesAnchorEl) || searchOpen || Boolean(exploreAnchorEl), sx: {
                zIndex: 1100,
                top: 129,
                backgroundColor: brandclub_colors.darkBlue3,
            } }),
        React.createElement(HeadBarContainer, null,
            React.createElement(HeaderBarInner, { container: true, wrap: "nowrap" },
                React.createElement(BarRight, { container: true, flexDirection: 'row', wrap: "nowrap", justifyContent: 'flex-end' },
                    React.createElement(LinkComponent, { to: '/getapp', className: "link" },
                        React.createElement(HeaderLink, null,
                            React.createElement(SmartphoneIcon, { color: "primary", sx: { fontSize: 21, marginRight: 0.75, marginLeft: 0.75 } }),
                            "Get the app")),
                    React.createElement(LinkComponent, { to: '/getapp', className: "link" },
                        React.createElement(HeaderLink, null,
                            React.createElement(BookmarkStarIcon, { color: "primary", sx: { fontSize: 21, marginRight: 0.75, marginLeft: 0.75 } }),
                            "Join the club"))))),
        React.createElement(NavigationContainer, { ref: ref },
            React.createElement(NavBarContainer, null,
                React.createElement(BarLeft, { container: true, width: 220 },
                    React.createElement(BrandLogo, null,
                        React.createElement(LinkComponent, { style: { display: 'flex' }, to: "".concat(routePrefix, "/home?").concat(persistentQueryParams || '') }, (brandEntity === null || brandEntity === void 0 ? void 0 : brandEntity.id) ? React.createElement("img", { loading: "lazy", src: getBrandLogo(brandEntity === null || brandEntity === void 0 ? void 0 : brandEntity.id), alt: "bd_logo" }) : null))),
                React.createElement(NavSearchButton, { onClose: handleSearchClose, onSubmitSearch: onSubmitSearch, searchInput: searchInput, setSearchInput: setSearchInput, handleClick: handleSearchOpen, open: searchOpen, axios: axios, useNavigate: useNavigate, useSearchParams: useSearchParams, persistentQueryParams: persistentQueryParams, pageContext: pageContext, mainEntity: mainEntity, brandEntity: brandEntity, filterDialogOpen: filterDialogOpen, setFilterDialogOpen: setFilterDialogOpen, filterCount: filterCount, setFilterCount: setFilterCount, routePrefix: routePrefix }),
                React.createElement("div", { style: { width: 44, display: 'flex', justifyContent: 'flex-end' } },
                    !searchOpen && (React.createElement(NavUserButton, { onClick: function () {
                            navigate('/dashboard/me');
                        } })),
                    searchOpen && React.createElement(NavSearchCloseButton, { onClick: handleSearchClose })),
                React.createElement(BarCenter, { container: true, wrap: "nowrap", style: { display: searchOpen ? 'none' : 'flex' } }, navMenu.map(function (nav, idx) {
                    var _a, _b, _c, _d, _e;
                    if (nav.name === 'Home') {
                        return null;
                    }
                    if (nav.name === 'Shop') {
                        return (React.createElement(React.Fragment, { key: idx },
                            React.createElement("div", { className: "nav_button text-link-wrapper", role: "button", onClick: handleCategoriesOpen },
                                React.createElement("div", { className: "text-link" }, "Shop")),
                            React.createElement(Popover, { open: Boolean(categoriesAnchorEl), anchorEl: categoriesAnchorEl, anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'center',
                                }, transformOrigin: {
                                    vertical: -13,
                                    horizontal: 'center',
                                }, PaperProps: {
                                    sx: __assign({ boxShadow: 'none', left: "0 !important", right: "0px !important", marginLeft: 'auto', marginRight: 'auto', width: '100vw', maxWidth: '100vw', maxHeight: 'calc(100vh - 210px)', borderRadius: '0' }, brandclubStyles.customScrollbar),
                                }, onClose: handleCategoriesClose },
                                React.createElement(CategoriesDropdown, { categoryNav: nav, isCustomGrouped: (_a = site.pages) === null || _a === void 0 ? void 0 : _a.some(function (p) { return p.pageType === BrandclubPageType.CustomCategoryGroup; }), LinkComponent: LinkComponent, handleCategoriesClose: handleCategoriesClose }))));
                    }
                    if (nav.name === 'Explore') {
                        var collectionsNav = (_b = nav === null || nav === void 0 ? void 0 : nav.subNav) === null || _b === void 0 ? void 0 : _b.find(function (item) { return item.name === 'Collections'; });
                        var editorialsNav = (_c = nav === null || nav === void 0 ? void 0 : nav.subNav) === null || _c === void 0 ? void 0 : _c.find(function (item) { return item.name === 'Editorials'; });
                        if (!((_d = collectionsNav === null || collectionsNav === void 0 ? void 0 : collectionsNav.subNav) === null || _d === void 0 ? void 0 : _d.length) && !((_e = editorialsNav === null || editorialsNav === void 0 ? void 0 : editorialsNav.subNav) === null || _e === void 0 ? void 0 : _e.length)) {
                            return null;
                        }
                        return (React.createElement(React.Fragment, { key: idx },
                            React.createElement("div", { className: "nav_button text-link-wrapper", role: "button", onClick: handleExploreOpen },
                                React.createElement("div", { className: "text-link" }, "Explore")),
                            React.createElement(Popover, { open: Boolean(exploreAnchorEl), anchorEl: exploreAnchorEl, anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'center',
                                }, transformOrigin: {
                                    vertical: -13,
                                    horizontal: 'center',
                                }, PaperProps: {
                                    sx: __assign({ boxShadow: 'none', left: "0 !important", right: "0px !important", marginLeft: 'auto', marginRight: 'auto', width: '100vw', maxWidth: '100vw', maxHeight: 'calc(100vh - 210px)', borderRadius: '0' }, brandclubStyles.customScrollbar),
                                }, onClose: handleExploreClose },
                                React.createElement(ExploreDropdown, { onClose: handleExploreClose, nav: nav, LinkComponent: LinkComponent }))));
                    }
                    return (React.createElement("div", { className: "nav_button", key: idx }, nav.route ? (React.createElement(LinkComponent, { style: { textDecoration: 'none', color: theme.palette.primary.main }, to: nav.route, className: "text-link-wrapper" },
                        React.createElement("div", { className: "text-link" }, nav.name))) : (nav.name)));
                })),
                React.createElement(React.Fragment, null,
                    React.createElement(Popover, { id: "search-popover", open: searchOpen, anchorEl: searchAnchorEl, anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'center',
                        }, transformOrigin: {
                            vertical: -13,
                            horizontal: 'center',
                        }, PaperProps: {
                            sx: __assign({ boxShadow: 'none', left: "0 !important", right: "0px !important", marginLeft: 'auto', marginRight: 'auto', width: '100vw', maxWidth: '100vw', maxHeight: 'calc(100vh - 210px)', borderRadius: '0' }, brandclubStyles.customScrollbar),
                        }, disableAutoFocus: true, disableEnforceFocus: true, disableRestoreFocus: true, onClose: handleSearchClose },
                        React.createElement(SearchDropdown, { brandEntity: brandEntity, onSubmitSearch: onSubmitSearch, onClose: handleSearchClose, axios: axios, searchInput: searchInput, brandId: brandId, LinkComponent: LinkComponent, apolloClient: apolloClient, abTestingTreatments: pageContext.abTestingTreatments }))))),
        React.createElement(ProductPageDialog, { popupDrawerProps: {
                open: popupOpen,
                onClose: handlePopupClose,
            }, showIcon: true, title: 'Membership rewards', subtitle: "Earn ".concat(hasJoinReward ? rewardAmount : "cash rewards", " when you join the ").concat(((_d = brandEntity === null || brandEntity === void 0 ? void 0 : brandEntity.entity) === null || _d === void 0 ? void 0 : _d.name) || '', " club. Access rewards in the Brandclub app."), phoneInput: {
                closeDialogText: "Continue shopping",
            }, video: {
                videoUrl: BC_VIDEOS.HOME_HEAD.video,
                coverImageUrl: BC_VIDEOS.HOME_HEAD.coverImage,
            }, Link: LinkComponent, mainEntity: mainEntity, trackingParams: trackingParams, handleSendText: handleSendText, analyticsTrack: analyticsTrack })));
};
var BrandclubNavigation = function (_a) {
    var props = __rest(_a, []);
    return React.createElement(BrandclubNavigationNew, __assign({}, props));
};

export { BrandclubNavigation, BrandclubNavigationNew };
//# sourceMappingURL=index.js.map
